import { ApiError, type TeamChannelDto, type TeamMessageDto } from "@bafe/api-client";
import {
	canDeleteTeamMessage,
	canEditTeamMessage,
	canInviteToTeamConversation,
	teamConversationKindCopy,
	teamConversationPeopleCopy,
	teamConversationTitle,
	teamMessageDeletedCopy,
} from "@bafe/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type CSSProperties, type FormEvent, type PointerEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { ArrowDown, ArrowUp, ChevronLeft, CloseCircle, Edit2, Gallery, More, Paperclip, Search, Send2, TickCircle, User, Users } from "reicon-react";

import { EngageAudioPlayer, EngageVoiceCapture, EngageVoiceMic } from "../../components/EngageVoice";
import { Popup } from "../../components/Popup";
import { api } from "../../lib/api";
import { dissolveElement, resetDissolve } from "../../lib/dissolve";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { applyTeamLiveMessage, isTeamLiveMessage } from "../../lib/team-live";
import { useSession } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";
import { isoDay, stamp } from "../../lib/when.js";

type KindFilter = "all" | "channel" | "direct";

export function TeamOverview() {
	return <TeamMessenger />;
}

export function TeamChannel() {
	return <TeamMessenger />;
}

export function TeamChannels() {
	return <Navigate to="/team" replace />;
}

export function TeamMessenger() {
	const { id } = useParams();
	const channelId = id ?? "";
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const session = useSession();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const selfId = session.data?.user.id ?? "";
	const logRef = useRef<HTMLOListElement>(null);
	const [query, setQuery] = useState("");
	const [filter, setFilter] = useState<KindFilter>("all");
	const [body, setBody] = useState("");
	const [newOpen, setNewOpen] = useState(false);
	const [open, setOpen] = useState(false);
	const [groupNameOpen, setGroupNameOpen] = useState(false);
	const [directOpen, setDirectOpen] = useState(false);
	const [inviteOpen, setInviteOpen] = useState(false);
	const [membersOpen, setMembersOpen] = useState(false);
	const [name, setName] = useState("");
	const [picked, setPicked] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [findOpen, setFindOpen] = useState(false);
	const [find, setFind] = useState("");
	const [hitId, setHitId] = useState("");
	const [menu, setMenu] = useState<{ id: string; name: string; x: number; y: number } | null>(null);
	const [msgMenu, setMsgMenu] = useState<{
		id: string;
		body: string;
		canEdit: boolean;
		x: number;
		y: number;
	} | null>(null);
	const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
	const [pendingEdit, setPendingEdit] = useState<{ id: string; body: string } | null>(null);
	const [pendingMsgDelete, setPendingMsgDelete] = useState<string | null>(null);
	const [vanished, setVanished] = useState<string[]>([]);
	const [hidden, setHidden] = useState<string[]>(() => readHidden(organisationId ?? ""));
	const [peopleQuery, setPeopleQuery] = useState("");
	const [attachOpen, setAttachOpen] = useState(false);
	const [voiceOpen, setVoiceOpen] = useState(false);
	const [drafts, setDrafts] = useState<TeamDraft[]>([]);
	const [viewingId, setViewingId] = useState<string | null>(null);
	const holdRef = useRef<number | null>(null);
	const ignoreClickRef = useRef(false);
	const fileRef = useRef<HTMLInputElement>(null);

	const channels = useQuery({ queryKey: queryKeys.teamChannels, queryFn: () => api.listTeamChannels() });
	const messages = useQuery({
		queryKey: queryKeys.teamMessages(channelId),
		queryFn: () => api.listTeamMessages(channelId),
		enabled: Boolean(channelId),
	});
	const members = useQuery({
		queryKey: queryKeys.teamMembers(channelId),
		queryFn: () => api.listTeamMembers(channelId),
		enabled: Boolean(channelId),
	});
	const people = useQuery({ queryKey: queryKeys.members, queryFn: () => api.listMembers() });

	useEffect(() => {
		if (!channelId) return;
		void api.markTeamRead(channelId).then(() => queryClient.invalidateQueries({ queryKey: queryKeys.teamChannels }));
	}, [channelId, queryClient]);

	useEffect(() => {
		setMembersOpen(false);
		setVanished([]);
		setVoiceOpen(false);
		setAttachOpen(false);
	}, [channelId]);

	useEffect(() => {
		if (!organisationId || typeof window === "undefined") return;
		const protocol = window.location.protocol === "https:" ? "wss" : "ws";
		const socket = new WebSocket(
			`${protocol}://${window.location.host}/api/v1/team/ws?organisationId=${encodeURIComponent(organisationId)}`,
		);
		socket.onmessage = (event) => {
			try {
				const payload = JSON.parse(String(event.data)) as unknown;
				if (!isTeamLiveMessage(payload)) return;
				applyTeamLiveMessage(queryClient, payload.channelId, payload.message);
			} catch {
				// A malformed frame is dropped. The next send still refreshes the list.
			}
		};
		return () => socket.close();
	}, [organisationId, queryClient]);

	useEffect(() => {
		if (findOpen && find.trim()) return;
		const log = logRef.current;
		if (!log) return;
		log.scrollTop = log.scrollHeight;
	}, [find, findOpen, messages.data]);

	const send = useMutation({
		mutationFn: async () => {
			if (!drafts.length) {
				const message = await api.sendTeamMessage(channelId, body.trim());
				applyTeamLiveMessage(queryClient, channelId, message);
				return;
			}
			const caption = body.trim();
			const leftover = [...drafts];
			const albumId = leftover.length > 1 ? crypto.randomUUID() : undefined;
			try {
				while (leftover.length) {
					const item = leftover[0];
					if (!item) break;
					const message = await api.sendTeamMedia(
						channelId,
						item.file,
						leftover.length === 1 ? caption : "",
						albumId,
					);
					applyTeamLiveMessage(queryClient, channelId, message);
					if (item.url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.url);
					leftover.shift();
				}
			} finally {
				setDrafts(leftover);
			}
		},
		onSuccess: () => setBody(""),
		onError: (caught) => setError(presentError(caught).title),
	});

	const sendVoice = useMutation({
		mutationFn: async (file: File) => {
			const caption = body.trim();
			const message = await api.sendTeamMedia(channelId, file, caption);
			applyTeamLiveMessage(queryClient, channelId, message);
		},
		onSuccess: () => setBody(""),
		onError: (caught) => setError(presentError(caught).title),
	});

	const create = useMutation({
		mutationFn: async () => {
			const result = await api.createTeamChannel(name.trim());
			await Promise.all(picked.map((userId) => api.inviteTeamMember(result.id, userId)));
			return result;
		},
		onSuccess: async (result) => {
			closeGroup();
			await queryClient.invalidateQueries({ queryKey: queryKeys.teamChannels });
			navigate(`/team/channels/${result.id}`);
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const direct = useMutation({
		mutationFn: (userId: string) => api.createTeamDirect(userId),
		onSuccess: async (result) => {
			setDirectOpen(false);
			setHidden((prev) => {
				const next = prev.filter((id) => id !== result.id);
				writeHidden(organisationId ?? "", next);
				return next;
			});
			await queryClient.invalidateQueries({ queryKey: queryKeys.teamChannels });
			navigate(`/team/channels/${result.id}`);
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const invite = useMutation({
		mutationFn: (userId: string) => api.inviteTeamMember(channelId, userId),
		onSuccess: async () => {
			setInviteOpen(false);
			await queryClient.invalidateQueries({ queryKey: queryKeys.teamMembers(channelId) });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const editMsg = useMutation({
		mutationFn: ({ id, body }: { id: string; body: string }) => api.editTeamMessage(channelId, id, body),
		onSuccess: (message) => {
			setPendingEdit(null);
			setBody("");
			applyTeamLiveMessage(queryClient, channelId, message);
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const deleteMsg = useMutation({
		mutationFn: (id: string) => api.deleteTeamMessage(channelId, id),
		onSuccess: (message) => {
			setPendingMsgDelete(null);
			applyTeamLiveMessage(queryClient, channelId, message);
		},
		onError: (caught, id) => {
			setVanished((current) => current.filter((item) => item !== id));
			setError(presentError(caught).title);
		},
	});

	const remove = useMutation({
		mutationFn: (id: string) => api.deleteTeamChannel(id),
		onSuccess: async (_, id) => {
			hideChannel(id);
			setPendingDelete(null);
			await queryClient.invalidateQueries({ queryKey: queryKeys.teamChannels });
			if (id === channelId) navigate("/team");
		},
		onError: (caught, id) => {
			if (caught instanceof ApiError && (caught.status === 404 || caught.status === 405)) {
				hideChannel(id);
				setPendingDelete(null);
				if (id === channelId) navigate("/team");
				return;
			}
			const row = document.querySelector(`[href$="/team/channels/${id}"], [href$="/team/${id}"]`)?.closest("li");
			resetDissolve(row instanceof HTMLElement ? row : null);
			setError(presentError(caught).title);
		},
	});

	const listed = useMemo(
		() => (channels.data ?? []).filter((channel) => !hidden.includes(channel.id)),
		[channels.data, hidden],
	);

	const visible = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return listed.filter((channel) => {
			if (filter !== "all" && channel.kind !== filter) return false;
			if (!needle) return true;
			return `${channel.name} ${channel.lastMessage ?? ""}`.toLowerCase().includes(needle);
		});
	}, [listed, filter, query]);

	const matches = useMemo(() => {
		const needle = find.trim().toLowerCase();
		if (!needle) return [];
		return (messages.data ?? []).filter(
			(message) => !message.deletedAt && message.body.toLowerCase().includes(needle),
		);
	}, [find, messages.data]);
	const hits = useMemo(() => [...matches].reverse(), [matches]);
	const matchKey = matches.map((message) => message.id).join(",");
	const mediaAlbum = useMemo(
		() =>
			(messages.data ?? []).filter(
				(message) => message.mediaKind && message.mediaKind !== "voice" && !message.deletedAt,
			),
		[messages.data],
	);

	const current = listed.find((channel) => channel.id === channelId);
	const currentTitle = current
		? teamConversationTitle(current.kind, current.name, selfId, members.data ?? [])
		: "Conversation";
	const invited = new Set(members.data?.map((member) => member.userId) ?? []);
	const guests = people.data?.filter((person) => !invited.has(person.userId)) ?? [];
	const roster = useMemo(() => {
		return [...(members.data ?? [])].sort((left, right) => {
			if (left.userId === selfId) return -1;
			if (right.userId === selfId) return 1;
			return left.fullName.localeCompare(right.fullName, undefined, { sensitivity: "base" });
		});
	}, [members.data, selfId]);
	const directory = (people.data ?? []).filter((person) => person.userId !== selfId);
	const directoryHits = directory.filter((person) =>
		person.fullName.toLowerCase().includes(peopleQuery.trim().toLowerCase()),
	);
	const chosen = directory.filter((person) => picked.includes(person.userId));
	const contacts = (people.data ?? []).filter((person) => {
		if (person.userId === selfId) return false;
		return !listed.some((channel) => channel.kind === "direct" && channel.name === person.fullName);
	});

	useEffect(() => {
		setHidden(readHidden(organisationId ?? ""));
	}, [organisationId]);

	useEffect(() => {
		if (!menu && !msgMenu) return;
		const close = () => {
			setMenu(null);
			setMsgMenu(null);
		};
		const timer = window.setTimeout(() => window.addEventListener("pointerdown", close), 0);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("pointerdown", close);
		};
	}, [menu, msgMenu]);

	useEffect(() => {
		setFind("");
		setFindOpen(false);
		setHitId("");
		setAttachOpen(false);
		setMsgMenu(null);
		setPendingEdit(null);
		setPendingMsgDelete(null);
		setViewingId(null);
		setBody("");
		clearDraft();
	}, [channelId]);

	useEffect(() => {
		if (!pendingEdit) return;
		const field = document.getElementById("team-message");
		if (field instanceof HTMLInputElement) {
			field.focus();
			field.setSelectionRange(field.value.length, field.value.length);
		}
	}, [pendingEdit]);

	useEffect(() => {
		if (!attachOpen) return;
		const close = () => setAttachOpen(false);
		const timer = window.setTimeout(() => window.addEventListener("pointerdown", close), 0);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("pointerdown", close);
		};
	}, [attachOpen]);

	useEffect(() => {
		if (!newOpen) return;
		const close = () => setNewOpen(false);
		const timer = window.setTimeout(() => window.addEventListener("pointerdown", close), 0);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("pointerdown", close);
		};
	}, [newOpen]);

	useEffect(() => {
		if (!findOpen || !matches.length) {
			if (!matches.length) setHitId("");
			return;
		}
		const latest = matches[matches.length - 1];
		if (!latest) return;
		setHitId(latest.id);
		requestAnimationFrame(() => {
			scrollToMessage(latest.id);
		});
	}, [findOpen, matchKey, matches]);

	function hideChannel(id: string) {
		setHidden((prev) => {
			if (prev.includes(id)) return prev;
			const next = [...prev, id];
			writeHidden(organisationId ?? "", next);
			return next;
		});
	}

	function clearHold() {
		if (holdRef.current !== null) window.clearTimeout(holdRef.current);
		holdRef.current = null;
	}

	function openMenu(channel: TeamChannelDto, x: number, y: number) {
		setMsgMenu(null);
		setMenu({ id: channel.id, name: channel.name, x, y });
	}

	function openMsgMenu(message: TeamMessageDto, x: number, y: number) {
		if (!canDeleteTeamMessage(message.authorId, selfId, message.deletedAt)) return;
		setMenu(null);
		setMsgMenu({
			id: message.id,
			body: message.body,
			canEdit: canEditTeamMessage(message.authorId, selfId, message.deletedAt, message.mediaKind),
			x,
			y,
		});
	}

	function startEdit(id: string, text: string) {
		clearDraft();
		setAttachOpen(false);
		setPendingEdit({ id, body: text });
		setBody(text);
		setMsgMenu(null);
	}

	function cancelEdit() {
		setPendingEdit(null);
		setBody("");
	}

	function onRowContextMenu(event: MouseEvent<HTMLAnchorElement>, channel: TeamChannelDto) {
		event.preventDefault();
		openMenu(channel, event.clientX, event.clientY);
	}

	function onRowPointerDown(event: PointerEvent<HTMLAnchorElement>, channel: TeamChannelDto) {
		if (event.pointerType === "mouse") return;
		const x = event.clientX;
		const y = event.clientY;
		clearHold();
		holdRef.current = window.setTimeout(() => {
			ignoreClickRef.current = true;
			openMenu(channel, x, y);
		}, 500);
	}

	function onRowClick(event: MouseEvent<HTMLAnchorElement>) {
		if (!ignoreClickRef.current) return;
		event.preventDefault();
		ignoreClickRef.current = false;
	}

	function jumpTo(id: string) {
		setHitId(id);
		scrollToMessage(id);
	}

	function stepHit(delta: number) {
		if (!matches.length) return;
		const index = matches.findIndex((message) => message.id === hitId);
		const next = matches[(index + delta + matches.length) % matches.length];
		if (next) jumpTo(next.id);
	}

	function closeFind() {
		setFindOpen(false);
		setFind("");
		setHitId("");
	}

	function clearDraft() {
		setDrafts((current) => {
			for (const item of current) {
				if (item.url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.url);
			}
			return [];
		});
	}

	function removeDraft(id: string) {
		setDrafts((current) => {
			const item = current.find((draft) => draft.id === id);
			if (item?.url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.url);
			return current.filter((draft) => draft.id !== id);
		});
	}

	function pickMedia(list: FileList | null) {
		setAttachOpen(false);
		if (!list?.length) return;
		const extra: TeamDraft[] = [];
		for (const file of Array.from(list)) {
			if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) continue;
			extra.push({
				id: `${file.name}-${file.size}-${file.lastModified}-${extra.length}`,
				file,
				kind: file.type.startsWith("video/") ? "video" : "photo",
				url: typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "",
			});
		}
		setDrafts((current) => {
			const next = [...current, ...extra].slice(0, TEAM_ALBUM_MAX);
			for (const item of extra) {
				if (!next.includes(item) && item.url && typeof URL.revokeObjectURL === "function") {
					URL.revokeObjectURL(item.url);
				}
			}
			return next;
		});
	}

	if (channels.isPending) return <p className="empty">Loading Team.</p>;
	if (channels.isError) return <p className="err">{presentError(channels.error).title}</p>;

	function closeGroup() {
		setOpen(false);
		setGroupNameOpen(false);
		setName("");
		setPicked([]);
		setPeopleQuery("");
	}

	function pickNew(kind: "group" | "direct") {
		setNewOpen(false);
		setPeopleQuery("");
		setPicked([]);
		setName("");
		setGroupNameOpen(false);
		if (kind === "group") setOpen(true);
		else setDirectOpen(true);
	}

	function togglePick(userId: string) {
		setPicked((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
	}

	return (
		<div className={channelId ? "team-app is-chat" : "team-app"}>
			<section className="team-list" aria-label="Conversations">
				<div className="team-list-head">
					<label className="team-search">
						<span className="sr">Search conversations</span>
						<Search size={16} aria-hidden="true" />
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Search"
							autoComplete="off"
						/>
					</label>
				</div>
				<div className="team-filters" role="tablist" aria-label="Conversation kind">
					<FilterTab current={filter} value="all" onSelect={setFilter}>
						All
					</FilterTab>
					<FilterTab current={filter} value="channel" onSelect={setFilter}>
						Groups
					</FilterTab>
					<FilterTab current={filter} value="direct" onSelect={setFilter}>
						Direct
					</FilterTab>
				</div>
				{error && <p className="err team-list-error">{error}</p>}
				<div className="team-scroll">
					{!listed.length ? (
						<p className="empty">No conversations yet. Start one for the people already in this workspace.</p>
					) : !visible.length ? (
						<p className="empty">
							{query.trim()
								? "No conversations match that search."
								: filter === "direct"
									? "No direct messages yet."
									: "No groups yet."}
						</p>
					) : (
						<ul className="team-rows">
							{visible.map((channel) => (
								<li key={channel.id}>
									<Link
										className={channel.id === channelId ? "team-row active" : "team-row"}
										data-kind={channel.kind}
										to={`/team/channels/${channel.id}`}
										onContextMenu={(event) => onRowContextMenu(event, channel)}
										onPointerDown={(event) => onRowPointerDown(event, channel)}
										onPointerUp={clearHold}
										onPointerCancel={clearHold}
										onPointerLeave={clearHold}
										onClick={onRowClick}
									>
										<span className={`team-avatar tone-${toneOf(channel.name)}`} aria-hidden="true">
											{initials(channel.name)}
										</span>
										<span className="team-row-main">
											<strong>{channel.name}</strong>
											<span>{channel.lastMessage ?? teamConversationKindCopy(channel.kind)}</span>
										</span>
										<span className="team-row-meta">
											{channel.lastMessageAt ? (
												<time dateTime={channel.lastMessageAt}>{listTime(channel.lastMessageAt)}</time>
											) : null}
											{channel.unreadCount > 0 && channel.id !== channelId ? (
												<span className="team-unread">{channel.unreadCount}</span>
											) : null}
										</span>
									</Link>
								</li>
							))}
						</ul>
					)}
					{!query.trim() && filter !== "channel" && contacts.length > 0 ? (
						<div className="team-contacts">
							<p>Contacts</p>
							<ul className="team-rows">
								{contacts.map((person) => (
									<li key={person.userId}>
										<button type="button" className="team-row" onClick={() => direct.mutate(person.userId)}>
											<span className={`team-avatar tone-${toneOf(person.fullName)}`} aria-hidden="true">
												{initials(person.fullName)}
											</span>
											<span className="team-row-main">
												<strong>{person.fullName}</strong>
												<span>Direct</span>
											</span>
										</button>
									</li>
								))}
							</ul>
						</div>
					) : null}
				</div>
				<div className="team-fab-wrap" onPointerDown={(event) => event.stopPropagation()}>
					{newOpen ? (
						<div className="team-new-menu" role="menu" aria-label="New conversation">
							<button type="button" role="menuitem" onClick={() => pickNew("group")}>
								<span className="team-new-ico group" aria-hidden="true">
									<Users size={18} />
								</span>
								New group
							</button>
							<button type="button" role="menuitem" onClick={() => pickNew("direct")}>
								<span className="team-new-ico direct" aria-hidden="true">
									<User size={18} />
								</span>
								Direct message
							</button>
						</div>
					) : null}
					<button
						type="button"
						className={newOpen ? "team-fab is-open" : "team-fab"}
						aria-label="New conversation"
						aria-expanded={newOpen}
						onClick={() => setNewOpen((value) => !value)}
					>
						{newOpen ? <CloseCircle size={22} aria-hidden="true" /> : <Edit2 size={20} aria-hidden="true" />}
					</button>
				</div>
				{open || directOpen ? (
					<div className="team-new-pane">
						{open && !groupNameOpen ? (
							<>
								<header>
									<button type="button" className="team-icon-btn" aria-label="Back" onClick={closeGroup}>
										<ChevronLeft size={22} aria-hidden="true" />
									</button>
									<h2>Add Members</h2>
								</header>
								<label className="team-search team-new-search">
									<span className="sr">Search people</span>
									<Search size={16} aria-hidden="true" />
									<input
										value={peopleQuery}
										onChange={(event) => setPeopleQuery(event.target.value)}
										placeholder="Search"
										autoComplete="off"
										autoFocus
									/>
								</label>
								{chosen.length > 0 ? (
									<ul className="team-picked">
										{chosen.map((person) => (
											<li key={person.userId}>
												<button type="button" onClick={() => togglePick(person.userId)}>
													<span className={`team-avatar tone-${toneOf(person.fullName)}`} aria-hidden="true">
														{initials(person.fullName)}
													</span>
													<span>{person.fullName.split(/\s+/)[0]}</span>
												</button>
											</li>
										))}
									</ul>
								) : null}
								{people.isPending && <p className="empty">Loading people.</p>}
								{people.isError && <p className="err">{presentError(people.error).title}</p>}
								{people.data && directoryHits.length === 0 ? (
									<p className="empty">
										{peopleQuery.trim()
											? "No one matches that search."
											: "No one else is in this workspace yet."}
									</p>
								) : null}
								{people.data ? (
									<ul className="team-rows">
										{directoryHits.map((member) => {
											const on = picked.includes(member.userId);
											return (
												<li key={member.userId}>
													<button
														type="button"
														className={on ? "team-row is-picked" : "team-row"}
														aria-pressed={on}
														onClick={() => togglePick(member.userId)}
													>
														<span className={`team-avatar tone-${toneOf(member.fullName)}`} aria-hidden="true">
															{initials(member.fullName)}
														</span>
														<span className="team-row-main">
															<strong>{member.fullName}</strong>
															<span>In this workspace</span>
														</span>
														<span className="team-check" aria-hidden="true">
															{on ? <TickCircle size={20} /> : null}
														</span>
													</button>
												</li>
											);
										})}
									</ul>
								) : null}
								<button
									type="button"
									className="team-fab"
									aria-label="Next"
									onClick={() => setGroupNameOpen(true)}
								>
									<TickCircle size={24} aria-hidden="true" />
								</button>
							</>
						) : open ? (
							<>
								<header>
									<button
										type="button"
										className="team-icon-btn"
										aria-label="Back"
										onClick={() => setGroupNameOpen(false)}
									>
										<ChevronLeft size={22} aria-hidden="true" />
									</button>
									<h2>New group</h2>
									<button
										className="team-text-btn"
										type="button"
										disabled={create.isPending || !name.trim()}
										onClick={() => create.mutate()}
									>
										{create.isPending ? "Creating" : "Create"}
									</button>
								</header>
								<form
									className="team-new-form"
									onSubmit={(event: FormEvent) => {
										event.preventDefault();
										if (name.trim()) create.mutate();
									}}
								>
									<label className="team-search team-new-search">
										<span className="sr">Name</span>
										<input
											value={name}
											onChange={(event) => setName(event.target.value)}
											required
											maxLength={80}
											placeholder="Group name"
											autoFocus
										/>
									</label>
									<p>{picked.length === 0 ? "Just you for now." : `${picked.length + 1} members`}</p>
								</form>
							</>
						) : (
							<>
								<header>
									<button
										type="button"
										className="team-icon-btn"
										aria-label="Back"
										onClick={() => setDirectOpen(false)}
									>
										<ChevronLeft size={22} aria-hidden="true" />
									</button>
									<label className="team-search">
										<span className="sr">Search people</span>
										<input
											value={peopleQuery}
											onChange={(event) => setPeopleQuery(event.target.value)}
											placeholder="Search"
											autoComplete="off"
											autoFocus
										/>
									</label>
								</header>
								{people.isPending && <p className="empty">Loading people.</p>}
								{people.isError && <p className="err">{presentError(people.error).title}</p>}
								{people.data ? (
									<ul className="team-rows">
										{directoryHits.map((member) => (
											<li key={member.userId}>
												<button type="button" className="team-row" onClick={() => direct.mutate(member.userId)}>
													<span className={`team-avatar tone-${toneOf(member.fullName)}`} aria-hidden="true">
														{initials(member.fullName)}
													</span>
													<span className="team-row-main">
														<strong>{member.fullName}</strong>
														<span>Direct</span>
													</span>
												</button>
											</li>
										))}
									</ul>
								) : null}
							</>
						)}
					</div>
				) : null}
			</section>
			<section className="team-chat" aria-label="Conversation">
				{!channelId ? (
					<p className="empty team-pick">Select a conversation.</p>
				) : (
					<div className="team-chat-col">
						<header
							className="team-chat-head"
							onContextMenu={(event) => {
								if (!current) return;
								event.preventDefault();
								openMenu(current, event.clientX, event.clientY);
							}}
						>
							<Link className="team-back" to="/team" aria-label="Back to conversations">
								<ChevronLeft size={20} aria-hidden="true" />
							</Link>
							{findOpen ? (
								<>
									<span className={`team-avatar tone-${toneOf(currentTitle)}`} aria-hidden="true">
										{initials(currentTitle)}
									</span>
									<label className="team-find">
										<Search size={16} aria-hidden="true" />
										<span className="sr">Search messages</span>
										<input
											value={find}
											onChange={(event) => setFind(event.target.value)}
											onKeyDown={(event) => {
												if (event.key === "ArrowUp") {
													event.preventDefault();
													stepHit(-1);
												} else if (event.key === "ArrowDown") {
													event.preventDefault();
													stepHit(1);
												} else if (event.key === "Escape") {
													event.preventDefault();
													closeFind();
												}
											}}
											placeholder="Search"
											autoComplete="off"
											autoFocus
										/>
									</label>
								</>
							) : (
								<>
									<button
										className="team-chat-info"
										type="button"
										aria-haspopup="dialog"
										aria-expanded={membersOpen}
										onClick={() => setMembersOpen(true)}
									>
										<span className={`team-avatar tone-${toneOf(currentTitle)}`} aria-hidden="true">
											{initials(currentTitle)}
										</span>
										<span className="team-chat-who" data-kind={current?.kind ?? ""}>
											<strong>{currentTitle}</strong>
											<span>
												{current ? teamConversationPeopleCopy(current.kind, current.memberCount) : ""}
											</span>
										</span>
									</button>
									{current && canInviteToTeamConversation(current.kind) ? (
										<button className="team-text-btn" type="button" onClick={() => setInviteOpen(true)}>
											Invite
										</button>
									) : null}
								</>
							)}
							{findOpen ? (
								<>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Previous match"
										disabled={!matches.length}
										onClick={() => stepHit(-1)}
									>
										<ArrowUp size={16} aria-hidden="true" />
									</button>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Next match"
										disabled={!matches.length}
										onClick={() => stepHit(1)}
									>
										<ArrowDown size={16} aria-hidden="true" />
									</button>
									<button className="team-icon-btn" type="button" aria-label="Close search" onClick={closeFind}>
										<CloseCircle size={18} aria-hidden="true" />
									</button>
								</>
							) : (
								<>
									<button className="team-icon-btn" type="button" aria-label="Search messages" onClick={() => setFindOpen(true)}>
										<Search size={18} aria-hidden="true" />
									</button>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Conversation menu"
										onClick={(event) => {
											if (!current) return;
											const box = event.currentTarget.getBoundingClientRect();
											openMenu(current, box.right - 168, box.bottom);
										}}
									>
										<More size={18} aria-hidden="true" />
									</button>
								</>
							)}
						</header>
						{findOpen && find.trim() ? (
							<ul className="team-hits" role="listbox" aria-label="Search results">
								{hits.length === 0 ? (
									<li className="team-hits-empty">No messages match that search.</li>
								) : (
									hits.map((message) => (
										<li key={message.id} role="presentation">
											<button
												type="button"
												role="option"
												aria-selected={message.id === hitId}
												className={message.id === hitId ? "team-hit active" : "team-hit"}
												onClick={() => jumpTo(message.id)}
											>
												<span className={`team-avatar tone-${toneOf(message.authorName)}`} aria-hidden="true">
													{initials(message.authorName)}
												</span>
												<span className="team-hit-main">
													<strong>{message.authorName}</strong>
													<span>{highlight(message.body, find)}</span>
												</span>
												<time dateTime={message.createdAt}>{listTime(message.createdAt)}</time>
											</button>
										</li>
									))
								)}
							</ul>
						) : null}
						<div className="team-thread">
							{messages.isPending ? (
								<p className="empty">Loading messages.</p>
							) : messages.isError ? (
								<p className="err">{presentError(messages.error).title}</p>
							) : !messages.data?.length ? (
								<p className="empty">No messages yet. Write the first one.</p>
							) : (
								<ol className="team-log" ref={logRef}>
									{threadItems(messages.data, selfId).map((item) =>
										item.kind === "album" && item.messages.every((message) => vanished.includes(message.id)) ? null : item.kind === "message" && vanished.includes(item.message.id) ? null : item.kind === "day" ? (
											<li className="team-day" key={item.id}>
												{item.label}
											</li>
										) : item.kind === "album" ? (
											<li
												className={`${item.mine ? "out" : "in"} has-media${item.messages.some((message) => message.id === hitId) ? " hit" : ""}`}
												id={`team-msg-${item.id}`}
												key={item.id}
											>
												{item.mine ? null : <strong>{item.messages[0]?.authorName}</strong>}
												<span className="bubble has-media">
													<div className={`team-album is-${Math.min(item.messages.length, 6)}`}>
														{item.messages.map((message) => (
															<TeamMedia
																key={message.id}
																channelId={channelId}
																message={message}
																onOpen={() => setViewingId(message.id)}
															/>
														))}
													</div>
													{albumCaption(item.messages) ? (
														<span className="team-bubble-body">
															{find.trim()
																? highlight(albumCaption(item.messages), find)
																: albumCaption(item.messages)}
														</span>
													) : null}
													<time dateTime={item.messages[item.messages.length - 1]?.createdAt}>
														{clockTime(item.messages[item.messages.length - 1]?.createdAt ?? "")}
													</time>
												</span>
											</li>
										) : (
											<li
												className={`${item.mine ? "out" : "in"}${item.message.id === hitId ? " hit" : ""}${pendingEdit?.id === item.message.id ? " is-editing" : ""}${item.message.mediaKind && !item.message.deletedAt ? " has-media" : ""}`}
												id={`team-msg-${item.message.id}`}
												key={item.message.id}
											>
												{item.mine ? null : <strong>{item.message.authorName}</strong>}
												<span
													className={`${item.message.deletedAt ? "bubble is-deleted" : "bubble"}${item.message.mediaKind && !item.message.deletedAt ? " has-media" : ""}`}
													onContextMenu={(event) => {
														if (
															!canEditTeamMessage(
																item.message.authorId,
																selfId,
																item.message.deletedAt,
																item.message.mediaKind,
															) &&
															!canDeleteTeamMessage(item.message.authorId, selfId, item.message.deletedAt)
														) {
															return;
														}
														event.preventDefault();
														event.stopPropagation();
														openMsgMenu(item.message, event.clientX, event.clientY);
													}}
													onPointerDown={(event) => {
														if (event.pointerType === "mouse") return;
														if (
															!canEditTeamMessage(
																item.message.authorId,
																selfId,
																item.message.deletedAt,
																item.message.mediaKind,
															) &&
															!canDeleteTeamMessage(item.message.authorId, selfId, item.message.deletedAt)
														) {
															return;
														}
														const x = event.clientX;
														const y = event.clientY;
														clearHold();
														holdRef.current = window.setTimeout(() => {
															openMsgMenu(item.message, x, y);
														}, 500);
													}}
													onPointerUp={clearHold}
													onPointerCancel={clearHold}
													onPointerLeave={clearHold}
												>
													{item.message.deletedAt ? (
														<span className="team-bubble-body">{teamMessageDeletedCopy()}</span>
													) : (
														<>
															{item.message.mediaKind ? (
																<TeamMedia
																	channelId={channelId}
																	message={item.message}
																	onOpen={() => setViewingId(item.message.id)}
																/>
															) : null}
															{item.message.body ? (
																<span className="team-bubble-body">
																	{find.trim() ? highlight(item.message.body, find) : item.message.body}
																</span>
															) : null}
														</>
													)}
													{item.message.editedAt && !item.message.deletedAt ? (
														<span className="team-edited">edited</span>
													) : null}
													<time dateTime={item.message.createdAt}>{clockTime(item.message.createdAt)}</time>
												</span>
							</li>
										),
									)}
					</ol>
				)}
						</div>
						<div className={pendingEdit ? "team-composer is-editing" : "team-composer"}>
							{pendingEdit ? (
								<div className="team-edit-bar">
									<span className="team-edit-mark" aria-hidden="true" />
									<div className="team-edit-copy">
										<strong>Editing</strong>
										<span>{pendingEdit.body}</span>
									</div>
									<button className="team-icon-btn" type="button" aria-label="Cancel edit" onClick={cancelEdit}>
										<CloseCircle size={18} aria-hidden="true" />
									</button>
								</div>
							) : null}
							{drafts.length > 0 && !pendingEdit ? (
								<div className="team-draft">
									<div className="team-draft-strip">
										{drafts.map((item) => (
											<span className="team-draft-tile" key={item.id}>
												{item.url && item.kind === "video" ? (
													<video className="team-draft-media" src={item.url} muted />
												) : item.url ? (
													<img className="team-draft-media" src={item.url} alt="" />
												) : null}
												<button
													type="button"
													className="team-icon-btn"
													aria-label="Remove attachment"
													onClick={() => removeDraft(item.id)}
												>
													<CloseCircle size={14} aria-hidden="true" />
												</button>
											</span>
										))}
									</div>
									<span>{draftCopy(drafts)}</span>
								</div>
							) : null}
							{voiceOpen && !pendingEdit ? (
								<EngageVoiceCapture
									disabled={sendVoice.isPending}
									onCancel={() => setVoiceOpen(false)}
									onError={(message) => {
										setVoiceOpen(false);
										setError(message);
									}}
									onRecorded={(file) => {
										setVoiceOpen(false);
										if (sendVoice.isPending) return;
										sendVoice.mutate(file);
									}}
								/>
							) : (
								<form
									className="team-compose"
									onSubmit={(event) => {
										event.preventDefault();
										if (pendingEdit) {
											if (body.trim()) editMsg.mutate({ id: pendingEdit.id, body: body.trim() });
											return;
										}
										if (drafts.length || body.trim()) send.mutate();
									}}
								>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Attach"
										aria-expanded={attachOpen}
										disabled={Boolean(pendingEdit)}
										onClick={() => setAttachOpen((open) => !open)}
									>
										<Paperclip size={18} aria-hidden="true" />
									</button>
									<label className="sr" htmlFor="team-message">
										Message
									</label>
									<input
										id="team-message"
										value={body}
										onChange={(event) => setBody(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === "Escape" && pendingEdit) {
												event.preventDefault();
												cancelEdit();
											}
										}}
										required={!drafts.length && !pendingEdit}
										maxLength={4000}
										autoComplete="off"
										placeholder="Message"
									/>
									{pendingEdit ? null : (
										<EngageVoiceMic
											disabled={send.isPending || sendVoice.isPending}
											onStart={() => {
												setAttachOpen(false);
												setVoiceOpen(true);
											}}
										/>
									)}
									<button
										className="team-send"
										type="submit"
										disabled={
											pendingEdit
												? editMsg.isPending || !body.trim()
												: send.isPending || sendVoice.isPending || (!drafts.length && !body.trim())
										}
										aria-label={
											pendingEdit
												? editMsg.isPending
													? "Saving"
													: "Save edit"
												: send.isPending
													? "Sending"
													: "Send"
										}
									>
										{pendingEdit ? <TickCircle size={20} aria-hidden="true" /> : <Send2 size={18} aria-hidden="true" />}
									</button>
								</form>
							)}
						</div>
						{attachOpen && !voiceOpen ? (
							<div
								className="team-attach-menu"
								role="menu"
								onPointerDown={(event) => event.stopPropagation()}
							>
								<label className="team-attach-pick" role="menuitem">
									<input
										ref={fileRef}
										className="team-file"
										type="file"
										accept="image/*,video/*"
										multiple
										onChange={(event) => {
											pickMedia(event.target.files);
											event.target.value = "";
											setAttachOpen(false);
										}}
									/>
									<Gallery size={18} aria-hidden="true" />
									Photo or Video
								</label>
							</div>
						) : null}
					</div>
				)}
			</section>
			{menu ? (
				<div
					className="team-row-menu"
					role="menu"
					style={{ top: menu.y, left: menu.x }}
					onPointerDown={(event) => event.stopPropagation()}
				>
					<button
						className="danger"
						type="button"
						role="menuitem"
						onClick={() => {
							setPendingDelete({ id: menu.id, name: menu.name });
							setMenu(null);
						}}
					>
						Delete
					</button>
				</div>
			) : null}
			{msgMenu ? (
				<div
					className="team-row-menu"
					role="menu"
					aria-label="Message"
					style={{ top: msgMenu.y, left: msgMenu.x }}
					onPointerDown={(event) => event.stopPropagation()}
				>
					{msgMenu.canEdit ? (
						<button
							type="button"
							role="menuitem"
							onClick={() => startEdit(msgMenu.id, msgMenu.body)}
						>
							Edit
						</button>
					) : null}
					<button
						className="danger"
						type="button"
						role="menuitem"
						onClick={() => {
							setPendingMsgDelete(msgMenu.id);
							setMsgMenu(null);
						}}
					>
						Delete
					</button>
				</div>
			) : null}
			<Popup
				open={Boolean(pendingMsgDelete)}
				title="Delete message"
				onClose={() => setPendingMsgDelete(null)}
			>
				<p className="sub">This message will be removed for everyone in the conversation.</p>
				<div className="form-actions">
					<button className="btn ghost" type="button" onClick={() => setPendingMsgDelete(null)}>
						Cancel
					</button>
					<button
						className="btn"
						type="button"
						disabled={deleteMsg.isPending}
						onClick={() => {
							const id = pendingMsgDelete;
							if (!id) return;
							setPendingMsgDelete(null);
							window.requestAnimationFrame(() => {
								const row = document.getElementById(`team-msg-${id}`);
								dissolveElement(row, {
									onDone: () => {
										setVanished((current) => (current.includes(id) ? current : [...current, id]));
										deleteMsg.mutate(id);
									},
								});
							});
						}}
					>
						{deleteMsg.isPending ? "Deleting" : "Delete"}
					</button>
				</div>
			</Popup>
			<Popup
				open={Boolean(pendingDelete)}
				title="Delete conversation"
				onClose={() => setPendingDelete(null)}
			>
				<p className="sub">This conversation will be removed from your list.</p>
				<div className="form-actions">
					<button className="btn ghost" type="button" onClick={() => setPendingDelete(null)}>
						Cancel
					</button>
					<button
						className="btn"
						type="button"
						disabled={remove.isPending}
						onClick={() => {
							const target = pendingDelete;
							if (!target) return;
							setPendingDelete(null);
							window.requestAnimationFrame(() => {
								const row = document
									.querySelector(`[href$="/team/channels/${target.id}"], [href$="/team/${target.id}"]`)
									?.closest("li");
								dissolveElement(row instanceof HTMLElement ? row : null, {
									onDone: () => remove.mutate(target.id),
								});
							});
						}}
					>
						{remove.isPending ? "Deleting" : "Delete"}
					</button>
				</div>
			</Popup>
			<Popup open={membersOpen} title="Members" onClose={() => setMembersOpen(false)}>
				{members.isPending && <p className="empty">Loading members.</p>}
				{members.isError && <p className="err">{presentError(members.error).title}</p>}
				{members.data && roster.length === 0 ? (
					<p className="empty">No one is in this conversation yet.</p>
				) : null}
				{roster.length > 0 ? (
					<ul className="team-member-list">
						{roster.map((member) => (
							<li key={member.userId}>
								<span className={`team-avatar tone-${toneOf(member.fullName)}`} aria-hidden="true">
									{initials(member.fullName)}
								</span>
								<span className="team-row-main">
									<strong>{member.fullName}</strong>
									<span>{member.userId === selfId ? "You" : member.email}</span>
								</span>
							</li>
						))}
					</ul>
				) : null}
			</Popup>
			<Popup open={inviteOpen} title="Invite to group" onClose={() => setInviteOpen(false)}>
					{people.isPending && <p className="empty">Loading people.</p>}
					{people.data && guests.length === 0 ? <p className="empty">Everyone in this workspace is already here.</p> : null}
					{people.data && (
						<ul className="stack">
							{guests.map((person) => (
								<li key={person.userId}>
									<button className="btn ghost" type="button" onClick={() => invite.mutate(person.userId)}>
										{person.fullName}
									</button>
								</li>
							))}
						</ul>
					)}
				</Popup>
			{viewingId && channelId ? (
				<TeamMediaViewer
					channelId={channelId}
					items={mediaAlbum}
					currentId={viewingId}
					onClose={() => setViewingId(null)}
					onSelect={setViewingId}
				/>
			) : null}
			</div>
	);
}

function FilterTab({
	current,
	value,
	onSelect,
	children,
}: {
	current: KindFilter;
	value: KindFilter;
	onSelect: (value: KindFilter) => void;
	children: string;
}) {
	return (
		<button type="button" role="tab" aria-selected={current === value} onClick={() => onSelect(value)}>
			{children}
		</button>
	);
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0];
	const second = parts[1];
	if (first && second) return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
	return (name.replace(/^#/, "").slice(0, 2) || "?").toUpperCase();
}

function toneOf(name: string): number {
	let n = 0;
	for (let i = 0; i < name.length; i += 1) n = (n * 31 + (name.charCodeAt(i) ?? 0)) >>> 0;
	return n % 6;
}

function parseWhen(value: string): Date | null {
	if (!value) return null;
	const raw = value.includes("T") ? value : value.replace(" ", "T");
	const date = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}+08:00`);
	return Number.isNaN(date.getTime()) ? null : date;
}

function clockTime(value: string): string {
	const date = parseWhen(value);
	if (!date) return value;
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(date);
}

function listTime(value: string): string {
	const date = parseWhen(value);
	if (!date) return value;
	const day = isoDay(date);
	if (day === isoDay(new Date())) {
		return new Intl.DateTimeFormat("en-GB", {
			timeZone: "Asia/Kuala_Lumpur",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		}).format(date);
	}
	if (day === isoDay(new Date(Date.now() - 86_400_000))) return "Yesterday";
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		day: "2-digit",
		month: "short",
	}).format(date);
}

function dayHeading(value: string): string {
	const day = isoDay(value);
	if (day && day === isoDay(new Date())) return "Today";
	if (day && day === isoDay(new Date(Date.now() - 86_400_000))) return "Yesterday";
	return stamp(value).replace(/,\s*\d{2}:\d{2}$/, "") || day;
}

function viewerWhen(value: string): string {
	return `${dayHeading(value)} at ${clockTime(value)}`;
}

function canWheelZoom(): boolean {
	return Boolean(typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)")?.matches);
}

function clampMediaZoom(value: number): number {
	return Math.min(4, Math.max(1, Math.round(value * 100) / 100));
}

function nextMediaZoom(current: number, deltaY: number): number {
	return clampMediaZoom(current * (deltaY < 0 ? 1.12 : 1 / 1.12));
}

function pinchZoom(startZoom: number, startDist: number, dist: number): number {
	return clampMediaZoom(startZoom * (dist / Math.max(startDist, 1)));
}

function touchDistance(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): number {
	return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

type TeamDraft = { id: string; file: File; kind: "photo" | "video"; url: string };

type ThreadItem =
	| { kind: "day"; id: string; label: string }
	| { kind: "message"; message: TeamMessageDto; mine: boolean }
	| { kind: "album"; id: string; messages: TeamMessageDto[]; mine: boolean };

const TEAM_ALBUM_MAX = 10;

function albumCaption(messages: TeamMessageDto[]): string {
	return messages.reduce((text, message) => message.body || text, "");
}

function draftCopy(drafts: TeamDraft[]): string {
	const photos = drafts.filter((item) => item.kind === "photo").length;
	const videos = drafts.filter((item) => item.kind === "video").length;
	if (photos === 1 && videos === 0) return "Photo";
	if (videos === 1 && photos === 0) return "Video";
	if (photos && videos) return `${photos} photos · ${videos} videos`;
	if (videos) return `${videos} videos`;
	return `${photos} photos`;
}

function canJoinAlbum(prev: TeamMessageDto, next: TeamMessageDto): boolean {
	if (!prev.mediaKind || !next.mediaKind || prev.deletedAt || next.deletedAt) return false;
	if (prev.mediaKind === "voice" || next.mediaKind === "voice") return false;
	if (prev.authorId !== next.authorId) return false;
	if (prev.albumId && next.albumId) return prev.albumId === next.albumId;
	if (prev.albumId || next.albumId) return false;
	const start = Date.parse(prev.createdAt);
	const end = Date.parse(next.createdAt);
	return Number.isFinite(start) && Number.isFinite(end) && end - start <= 300_000;
}

function threadItems(messages: TeamMessageDto[], selfId: string): ThreadItem[] {
	const items: ThreadItem[] = [];
	let lastDay = "";
	let pending: TeamMessageDto[] = [];
	let pendingMine = false;

	function flush() {
		const first = pending[0];
		if (!first) return;
		if (pending.length === 1) {
			items.push({ kind: "message", message: first, mine: pendingMine });
		} else {
			items.push({ kind: "album", id: first.id, messages: pending, mine: pendingMine });
		}
		pending = [];
	}

	for (const message of messages) {
		if (message.deletedAt) continue;
		const day = isoDay(message.createdAt);
		if (day && day !== lastDay) {
			flush();
			items.push({ kind: "day", id: `day-${day}`, label: dayHeading(message.createdAt) });
			lastDay = day;
		}
		const mine = Boolean(selfId) && message.authorId === selfId;
		const albumable = Boolean(message.mediaKind && message.mediaKind !== "voice" && !message.deletedAt);
		const last = pending[pending.length - 1];
		if (albumable && last && pending.length < TEAM_ALBUM_MAX && canJoinAlbum(last, message)) {
			pending.push(message);
			continue;
		}
		flush();
		if (albumable) {
			pending = [message];
			pendingMine = mine;
			continue;
		}
		items.push({ kind: "message", message, mine });
	}
	flush();
	return items;
}

function scrollToMessage(id: string) {
	const node = document.getElementById(`team-msg-${id}`);
	if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ block: "center" });
}

function TeamMedia({
	channelId,
	message,
	onOpen,
}: {
	channelId: string;
	message: TeamMessageDto;
	onOpen: () => void;
}) {
	const [url, setUrl] = useState("");
	useEffect(() => {
		if (!message.mediaKind) return;
		let objectUrl = "";
		let gone = false;
		void (async () => {
			for (let attempt = 0; attempt < 4; attempt += 1) {
				try {
					const blob = await api.readTeamMedia(channelId, message.id);
					if (gone) return;
					objectUrl = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
					setUrl(objectUrl);
					return;
				} catch {
					await new Promise((resolve) => window.setTimeout(resolve, 280 * (attempt + 1)));
				}
			}
		})();
		return () => {
			gone = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [channelId, message.id, message.mediaKind]);
	if (!url) {
		return (
			<span className="team-media-wait">
				{message.mediaKind === "video" ? "Video" : message.mediaKind === "voice" ? "Voice message" : "Photo"}
			</span>
		);
	}
	if (message.mediaKind === "voice") {
		return <EngageAudioPlayer url={url} />;
	}
	if (message.mediaKind === "video") {
		return (
			<button className="team-media-open" type="button" aria-label="Open video" onClick={onOpen}>
				<video className="team-media" src={url} muted playsInline preload="metadata" />
			</button>
		);
	}
	return (
		<button className="team-media-open" type="button" aria-label="Open photo" onClick={onOpen}>
			<img className="team-media" src={url} alt={message.mediaName || "Photo"} />
		</button>
	);
}

function TeamMediaViewer({
	channelId,
	items,
	currentId,
	onClose,
	onSelect,
}: {
	channelId: string;
	items: TeamMessageDto[];
	currentId: string;
	onClose: () => void;
	onSelect: (id: string) => void;
}) {
	const index = Math.max(0, items.findIndex((item) => item.id === currentId));
	const message = items[index];
	const [url, setUrl] = useState("");
	const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
	const viewRef = useRef(view);
	const gestureRef = useRef<
		| { kind: "pan"; lastX: number; lastY: number }
		| {
				kind: "pinch";
				startZoom: number;
				startX: number;
				startY: number;
				startDist: number;
				startMidX: number;
				startMidY: number;
		  }
		| null
	>(null);
	const stageRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!items.length || !items.some((item) => item.id === currentId)) onClose();
	}, [currentId, items, onClose]);
	useEffect(() => {
		const next = { zoom: 1, x: 0, y: 0 };
		viewRef.current = next;
		setView(next);
		gestureRef.current = null;
	}, [message?.id]);
	useEffect(() => {
		if (!message) return;
		let objectUrl = "";
		let gone = false;
		setUrl("");
		void (async () => {
			for (let attempt = 0; attempt < 4; attempt += 1) {
				try {
					const blob = await api.readTeamMedia(channelId, message.id);
					if (gone) return;
					objectUrl = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
					setUrl(objectUrl);
					return;
				} catch {
					await new Promise((resolve) => window.setTimeout(resolve, 280 * (attempt + 1)));
				}
			}
		})();
		return () => {
			gone = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [channelId, message?.id]);
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
			if (event.key === "ArrowLeft") {
				const next = items[index - 1];
				if (next) onSelect(next.id);
			}
			if (event.key === "ArrowRight") {
				const next = items[index + 1];
				if (next) onSelect(next.id);
			}
		};
		window.addEventListener("keydown", onKey);
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = previous;
		};
	}, [index, items, onClose, onSelect]);
	useEffect(() => {
		const stage = stageRef.current;
		if (!stage) return;
		const apply = (next: { zoom: number; x: number; y: number }) => {
			const clean = next.zoom <= 1 ? { zoom: 1, x: 0, y: 0 } : next;
			viewRef.current = clean;
			setView(clean);
		};
		const onWheel = (event: WheelEvent) => {
			if (!canWheelZoom()) return;
			event.preventDefault();
			const current = viewRef.current;
			apply({ ...current, zoom: nextMediaZoom(current.zoom, event.deltaY) });
		};
		const onPointerDown = (event: globalThis.PointerEvent) => {
			if (event.pointerType === "touch") return;
			if (viewRef.current.zoom <= 1) return;
			if (!(event.target instanceof Element) || !event.target.closest("img, video")) return;
			gestureRef.current = { kind: "pan", lastX: event.clientX, lastY: event.clientY };
			if (typeof stage.setPointerCapture === "function") stage.setPointerCapture(event.pointerId);
		};
		const onPointerMove = (event: globalThis.PointerEvent) => {
			const gesture = gestureRef.current;
			if (!gesture || gesture.kind !== "pan") return;
			const current = viewRef.current;
			apply({ ...current, x: current.x + event.clientX - gesture.lastX, y: current.y + event.clientY - gesture.lastY });
			gesture.lastX = event.clientX;
			gesture.lastY = event.clientY;
		};
		const endPan = () => {
			if (gestureRef.current?.kind === "pan") gestureRef.current = null;
		};
		const onTouchStart = (event: TouchEvent) => {
			const first = event.touches[0];
			const second = event.touches[1];
			if (first && second) {
				event.preventDefault();
				gestureRef.current = {
					kind: "pinch",
					startZoom: viewRef.current.zoom,
					startX: viewRef.current.x,
					startY: viewRef.current.y,
					startDist: touchDistance(first, second),
					startMidX: (first.clientX + second.clientX) / 2,
					startMidY: (first.clientY + second.clientY) / 2,
				};
				return;
			}
			if (first && viewRef.current.zoom > 1) {
				gestureRef.current = { kind: "pan", lastX: first.clientX, lastY: first.clientY };
			}
		};
		const onTouchMove = (event: TouchEvent) => {
			const gesture = gestureRef.current;
			if (!gesture) return;
			const first = event.touches[0];
			const second = event.touches[1];
			if (gesture.kind === "pinch" && first && second) {
				event.preventDefault();
				apply({
					zoom: pinchZoom(gesture.startZoom, gesture.startDist, touchDistance(first, second)),
					x: gesture.startX + (first.clientX + second.clientX) / 2 - gesture.startMidX,
					y: gesture.startY + (first.clientY + second.clientY) / 2 - gesture.startMidY,
				});
				return;
			}
			if (gesture.kind === "pan" && first) {
				event.preventDefault();
				apply({
					...viewRef.current,
					x: viewRef.current.x + first.clientX - gesture.lastX,
					y: viewRef.current.y + first.clientY - gesture.lastY,
				});
				gesture.lastX = first.clientX;
				gesture.lastY = first.clientY;
			}
		};
		const onTouchEnd = (event: TouchEvent) => {
			if (event.touches.length < 2 && gestureRef.current?.kind === "pinch") gestureRef.current = null;
			if (event.touches.length === 0) gestureRef.current = null;
		};
		stage.addEventListener("wheel", onWheel, { passive: false });
		stage.addEventListener("pointerdown", onPointerDown);
		stage.addEventListener("pointermove", onPointerMove);
		stage.addEventListener("pointerup", endPan);
		stage.addEventListener("pointercancel", endPan);
		stage.addEventListener("touchstart", onTouchStart, { passive: false });
		stage.addEventListener("touchmove", onTouchMove, { passive: false });
		stage.addEventListener("touchend", onTouchEnd);
		stage.addEventListener("touchcancel", onTouchEnd);
		return () => {
			stage.removeEventListener("wheel", onWheel);
			stage.removeEventListener("pointerdown", onPointerDown);
			stage.removeEventListener("pointermove", onPointerMove);
			stage.removeEventListener("pointerup", endPan);
			stage.removeEventListener("pointercancel", endPan);
			stage.removeEventListener("touchstart", onTouchStart);
			stage.removeEventListener("touchmove", onTouchMove);
			stage.removeEventListener("touchend", onTouchEnd);
			stage.removeEventListener("touchcancel", onTouchEnd);
		};
	}, [url]);
	function step(delta: number) {
		const next = items[index + delta];
		if (next) onSelect(next.id);
	}
	if (!message) return null;
	const kind = message.mediaKind === "video" ? "Video" : "Photo";
	return (
		<div
			className="team-media-view"
			role="dialog"
			aria-modal="true"
			aria-label={kind}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<div className="team-media-topbar">
				<span className={`team-avatar tone-${toneOf(message.authorName)}`} aria-hidden="true">
					{initials(message.authorName)}
				</span>
				<div className="team-media-who">
					<strong>{message.authorName}</strong>
					<span>
						{index + 1} of {items.length}
						<i className="team-media-dot" aria-hidden="true" />
						{viewerWhen(message.createdAt)}
					</span>
				</div>
				{url ? (
					<a className="team-icon-btn" href={url} download={message.mediaName || kind.toLowerCase()} aria-label="Download">
						<ArrowDown size={20} aria-hidden="true" />
					</a>
				) : null}
				<button className="team-icon-btn" type="button" aria-label={`Close ${kind.toLowerCase()}`} onClick={onClose}>
					<span className="team-media-x" aria-hidden="true" />
				</button>
			</div>
			<div
				ref={stageRef}
				className={view.zoom > 1 ? "team-media-stage is-zoom" : "team-media-stage"}
				style={
					{
						"--team-media-zoom": String(view.zoom),
						"--team-media-x": `${view.x}px`,
						"--team-media-y": `${view.y}px`,
					} as CSSProperties
				}
				onClick={(event) => {
					if (event.target === event.currentTarget && view.zoom <= 1) onClose();
				}}
			>
				{items.length > 1 ? (
					<button
						className="team-media-nav"
						type="button"
						aria-label="Previous"
						disabled={index === 0}
						onClick={() => step(-1)}
					>
						<ChevronLeft size={28} aria-hidden="true" />
					</button>
				) : null}
				{url ? (
					message.mediaKind === "video" ? (
						<video src={url} controls autoPlay playsInline />
					) : (
						<img src={url} alt={message.mediaName || "Photo"} draggable={false} />
					)
				) : (
					<span className="team-media-wait">{kind}</span>
				)}
				{items.length > 1 ? (
					<button
						className="team-media-nav is-next"
						type="button"
						aria-label="Next"
						disabled={index === items.length - 1}
						onClick={() => step(1)}
					>
						<ChevronLeft size={28} aria-hidden="true" />
					</button>
				) : null}
			</div>
			{message.body ? <p className="team-media-caption">{message.body}</p> : null}
		</div>
	);
}

function highlight(text: string, needle: string) {
	const query = needle.trim();
	if (!query) return text;
	const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	return text.split(new RegExp(`(${escaped})`, "ig")).map((chunk, index) =>
		chunk.toLowerCase() === query.toLowerCase() ? <mark key={index}>{chunk}</mark> : chunk,
	);
}

function hiddenKey(organisationId: string): string {
	return `bafe.team.hidden.${organisationId || "none"}`;
}

function readHidden(organisationId: string): string[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(hiddenKey(organisationId));
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
	} catch {
		return [];
	}
}

function writeHidden(organisationId: string, ids: string[]): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(hiddenKey(organisationId), JSON.stringify(ids));
}
